import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, TimePicker, Switch } from 'antd';
import AntdFormItem from '../../../../../../../components/AntdFormItem';
import AntdInput from '../../../../../../../components/AntdInput';
import AntdSelect from '../../../../../../../components/AntdSelect';
import styles from './index.module.scss';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

function timeFieldValidator({
  intl,
  model,
  getFieldValue,
  value,
  fieldComparisons = [],
}) {
  if (!value || !model.includes('delivery')) {
    return Promise.resolve();
  }
  const errors = [];
  fieldComparisons.forEach((comp) => {
    const otherValue = getFieldValue(comp.field);
    const valueDayjs = dayjs(value);
    const otherDayjs = dayjs(otherValue);
    if (!valueDayjs.isValid() || (otherValue && !otherDayjs.isValid())) {
      return;
    }
    if (comp.condition(valueDayjs, otherDayjs)) {
      errors.push(intl.formatMessage(comp.message, comp.messageValues || {}));
    }
  });
  return errors.length > 0
    ? Promise.reject(new Error(errors.join('; ')))
    : Promise.resolve();
}

function requiredRule(intl, model) {
  return {
    required: model === 'delivery',
    message: intl.formatMessage({ id: 'validations.required' }),
  };
}

function numericRules({ intl, required, min, max }) {
  return [
    required && {
      required: true,
      message: intl.formatMessage({ id: 'validations.required' }),
    },
    {
      type: 'number',
      min,
      max,
      message: intl.formatMessage(
        { id: 'validations.number.between' },
        { min, max },
      ),
      transform: (value) => Number(value),
    },
    {
      pattern: /^\d+$/,
      message: intl.formatMessage({ id: 'validations.format.integer' }),
    },
  ].filter(Boolean);
}

const DeliveryFormFields = ({ model, isAssayedAndLotified }) => {
  const intl = useIntl();
  const form = Form.useFormInstance();
  const mashControl = Form.useWatch('mash_control', form);
  const elementsOptions = [
    { value: 'Pilares', label: 'Pilares' },
    { value: 'Muros portantes', label: 'Muros portantes' },
    { value: 'Pilotes', label: 'Pilotes' },
    { value: 'Pilas', label: 'Pilas' },
    { value: 'Vigas', label: 'Vigas' },
    { value: 'Forjados', label: 'Forjados' },
    { value: 'Tableros puente', label: 'Tableros puente' },
    { value: 'Muros contención', label: 'Muros contención' },
    { value: 'Zapatas', label: 'Zapatas' },
    { value: 'Losas', label: 'Losas' },
    { value: 'Estribos puente', label: 'Estribos puente' },
    { value: 'Bloques', label: 'Bloques' },
    { value: 'Losas marco', label: 'Losas marco' },
    { value: 'Riostras', label: 'Riostras' },
    { value: 'Encepados', label: 'Encepados' },
    { value: 'Pantallas', label: 'Pantallas' },
    { value: 'Micropilotes', label: 'Micropilotes' },
  ];

  useEffect(() => {
    if (mashControl) {
      form.validateFields(['specimens_number', 'slump']);
    }
  }, [form, mashControl]);

  return (
    <>
      <div className={styles.timeFieldsRow}>
        <div className="col-xs-12 col-md-4">
          <AntdFormItem
            dependencies={[
              'start_discharging_time',
              'finish_discharging_time',
              'manufacture_time',
              'limit_time',
            ]}
            name="arriving_time"
            label={intl.formatMessage({
              id: 'pages.ocr.scannedDocuments.create.delivery.deliveryFormFields.arrivingTime.label',
            })}
          >
            <TimePicker size="large" format="HH:mm" style={{ width: '100%' }} />
          </AntdFormItem>
        </div>
        <div className="col-xs-12 col-md-4">
          <AntdFormItem
            dependencies={[
              'start_discharging_time',
              'finish_discharging_time',
              'manufacture_time',
              'limit_time',
              'arriving_time',
            ]}
            name="start_discharging_time"
            label={intl.formatMessage({
              id: 'pages.ocr.scannedDocuments.create.delivery.deliveryFormFields.startDischargingTime.label',
            })}
          >
            <TimePicker size="large" format="HH:mm" style={{ width: '100%' }} />
          </AntdFormItem>
        </div>
        <div className="col-xs-12 col-md-4">
          <AntdFormItem
            dependencies={[
              'start_discharging_time',
              'finish_discharging_time',
              'manufacture_time',
              'limit_time',
              'arriving_time',
            ]}
            name="finish_discharging_time"
            label={intl.formatMessage({
              id: 'pages.ocr.scannedDocuments.create.delivery.deliveryFormFields.finishDischargingTime.label',
            })}
          >
            <TimePicker size="large" format="HH:mm" style={{ width: '100%' }} />
          </AntdFormItem>
        </div>
      </div>

      {model === 'delivery' && (
        <div className={styles.mashControlRow}>
          <div className="col-xs-12 col-md-4">
            <AntdFormItem
              name="mash_control"
              label={intl.formatMessage({
                id: 'pages.ocr.scannedDocuments.create.delivery.deliveryFormFields.mashControl.label',
              })}
              valuePropName="checked"
            >
              <Switch
                onChange={(checked) => {
                  if (!checked) {
                    form.setFieldValue('specimens_number', undefined);
                    form.setFieldValue('slump', undefined);
                  }
                }}
                disabled={isAssayedAndLotified}
              />
            </AntdFormItem>
          </div>
          <div className="col-xs-12 col-md-4">
            <AntdFormItem
              name="specimens_number"
              label={intl.formatMessage({
                id: 'pages.ocr.scannedDocuments.create.delivery.deliveryFormFields.specimensNumber.label',
              })}
              rules={
                mashControl
                  ? numericRules({
                      intl,
                      required: true,
                      min: 2,
                      max: 20,
                    })
                  : []
              }
            >
              <AntdInput size="large" type="number" disabled={!mashControl} />
            </AntdFormItem>
          </div>
          <div className="col-xs-12 col-md-4">
            <AntdFormItem
              name="slump"
              label={intl.formatMessage({
                id: 'pages.ocr.scannedDocuments.create.delivery.deliveryFormFields.slump.label',
              })}
              dependencies={['mash_control']}
              rules={
                mashControl
                  ? numericRules({
                      intl,
                      required: true,
                      min: 0,
                      max: 220,
                    })
                  : []
              }
            >
              <AntdInput size="large" type="number" disabled={!mashControl} />
            </AntdFormItem>
          </div>
          {/* <div className="col-xs-12 col-md-3">
                    <AntdFormItem
                      name="mash_control_extra"
                      label={intl.formatMessage({
                        id: 'pages.ocr.scannedDocuments.create.delivery.deliveryFormFields.mashControlExtra.label',
                      })}
                      valuePropName="checked"
                    >
                      <Switch disabled={!mashControl} />
                    </AntdFormItem>
                  </div> */}
        </div>
      )}

      {/* Campos de elementos y ubicación */}
      <div className={styles.elementsLocationRow}>
        <div className="col-xs-12 col-md-6">
          <Form.Item dependencies={['elements']} noStyle>
            {({ getFieldValue }) => {
              const selectedElements = getFieldValue('elements') || [];

              return (
                <AntdFormItem
                  name="elements"
                  label={intl.formatMessage({
                    id: 'pages.ocr.scannedDocuments.create.delivery.deliveryFormFields.elements.label',
                  })}
                  rules={[
                    {
                      required: model === 'delivery',
                      message: intl.formatMessage({
                        id: 'validations.required',
                      }),
                    },
                  ]}
                >
                  <AntdSelect
                    size="small"
                    mode="multiple"
                    placeholder={intl.formatMessage({
                      id: 'pages.ocr.scannedDocuments.create.delivery.deliveryFormFields.elements.placeholder',
                    })}
                    tagRender={({ label, value }) => {
                      const currentIndex = selectedElements.findIndex(
                        (val) => val === value,
                      );
                      const isLast =
                        currentIndex === selectedElements.length - 1;

                      return (
                        <span
                          style={{
                            marginRight: isLast ? 0 : '8px',
                          }}
                        >
                          {label}
                          {!isLast && ','}
                        </span>
                      );
                    }}
                    options={elementsOptions}
                  />
                </AntdFormItem>
              );
            }}
          </Form.Item>
        </div>
        <div className="col-xs-12 col-md-6">
          <AntdFormItem
            name="detailed_location"
            label={intl.formatMessage({
              id: 'pages.ocr.scannedDocuments.create.delivery.deliveryFormFields.detailedLocation.label',
            })}
            rules={[
              {
                required: model === 'delivery',
                message: intl.formatMessage({ id: 'validations.required' }),
              },
              {
                whitespace: true,
                message: intl.formatMessage({
                  id: 'validations.no.whitespace.only',
                }),
              },
              {
                max: 60,
                message: intl.formatMessage(
                  {
                    id: 'validations.number.maxDigits',
                  },
                  { max: 60 },
                ),
              },
            ]}
          >
            <AntdInput
              size="large"
              placeholder="Ubicación"
              style={{ height: 47 }}
            />
          </AntdFormItem>
        </div>
      </div>
    </>
  );
};

DeliveryFormFields.propTypes = {
  model: PropTypes.string.isRequired,
  isAssayedAndLotified: PropTypes.bool,
};

DeliveryFormFields.defaultProps = {
  isAssayedAndLotified: false,
};

DeliveryFormFields.defaultProps = {};

export default DeliveryFormFields;
